// src/stores/circlesStore.js - Friend Circles state
//
// The circles this person is in, and which one they're looking at. What a
// circle holds is in FriendCirclesService.js; this keeps the list in step
// after anything changes it.
//
// No analytics here: circle events used to go to Google Analytics with the
// circle's name in them.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import friendCirclesService from '../services/circles/FriendCirclesService';

export const useCirclesStore = create(
  persist(
    (set, get) => ({
      myCircles: [],
      activeCircleId: null,
      isLoading: false,
      error: null,

      getActiveCircle: () => {
        const state = get();
        return state.myCircles.find((c) => c.id === state.activeCircleId) || null;
      },

      setActiveCircle: (circleId) => {
        set({ activeCircleId: circleId });
      },

      loadMyCircles: async (userId) => {
        set({ isLoading: true, error: null });
        const result = await friendCirclesService.getUserCircles(userId);
        if (result.success) {
          set((state) => {
            const stillIn = result.circles.some((c) => c.id === state.activeCircleId);
            return {
              myCircles: result.circles,
              // Don't keep pointing at a circle you've left or that has closed.
              activeCircleId: stillIn ? state.activeCircleId : (result.circles[0]?.id || null),
              isLoading: false,
            };
          });
        } else {
          set({ error: result.error, isLoading: false });
        }
        return result;
      },

      // Replace one circle with its current state, after a change to it.
      refreshCircle: async (circleId) => {
        const result = await friendCirclesService.getCircle(circleId);
        if (result.success) {
          set((state) => ({
            myCircles: state.myCircles.map((c) => (c.id === circleId ? result.circle : c)),
          }));
        }
        return result;
      },

      createCircle: async (userId, name, emoji, username = null) => {
        set({ isLoading: true, error: null });
        const result = await friendCirclesService.createCircle(userId, name, emoji, username);
        if (result.success) {
          set((state) => ({
            myCircles: [...state.myCircles, result.circle],
            activeCircleId: result.circleId,
            isLoading: false,
          }));
          return { success: true, inviteCode: result.inviteCode, circle: result.circle, circleId: result.circleId };
        }
        set({ error: result.error, isLoading: false });
        return { success: false, error: result.error };
      },

      // Having the code is the invitation: this either joins or fails. There
      // is no "request sent, waiting for approval" any more.
      joinCircle: async (userId, inviteCode, username) => {
        set({ isLoading: true, error: null });
        const result = await friendCirclesService.joinCircle(userId, inviteCode, username);
        if (!result.success) {
          set({ error: result.error, isLoading: false });
          return { success: false, error: result.error };
        }
        await get().loadMyCircles(userId);
        if (result.circleId && get().myCircles.some((c) => c.id === result.circleId)) {
          set({ activeCircleId: result.circleId });
        }
        set({ isLoading: false });
        return { success: true, alreadyMember: result.alreadyMember, circleName: result.circleName };
      },

      leaveCircle: async (userId, circleId) => {
        set({ isLoading: true, error: null });
        const result = await friendCirclesService.leaveCircle(userId, circleId);
        if (result.success) {
          set((state) => {
            const remaining = state.myCircles.filter((c) => c.id !== circleId);
            return {
              myCircles: remaining,
              activeCircleId: state.activeCircleId === circleId
                ? (remaining[0]?.id || null)
                : state.activeCircleId,
              isLoading: false,
            };
          });
          return { success: true, closed: result.closed };
        }
        set({ error: result.error, isLoading: false });
        return { success: false, error: result.error };
      },

      rotateInviteCode: async (circleId) => {
        const result = await friendCirclesService.rotateInviteCode(circleId);
        if (result.success) {
          set((state) => ({
            myCircles: state.myCircles.map((c) => (
              c.id === circleId ? { ...c, inviteCode: result.inviteCode } : c
            )),
          }));
        }
        return result;
      },

      clearError: () => set({ error: null }),

      reset: () => set({
        myCircles: [],
        activeCircleId: null,
        isLoading: false,
        error: null,
      }),
    }),
    {
      name: 'friend-circles-storage',
      partialize: (state) => ({
        myCircles: state.myCircles,
        activeCircleId: state.activeCircleId,
      }),
    }
  )
);

// Hook alias for convenience
export const useFriendCircles = useCirclesStore;
